import type {
  KbGenerationSessionDetail,
  SectionAiResult,
  SectionAiSelection,
} from "../types/gameRuleKbGenerate";
import { deriveAiSelectionOrder } from "./generateFlowSelectionOrder";

export type GenerateWizardStep = 1 | 2 | 3 | 4 | 5;

export type HydratedGenerationSessionState = {
  readonly sessionId: string;
  readonly step: GenerateWizardStep;
  readonly gameRuleTitle: string;
  readonly gameRuleName: string;
  readonly selectedSectionIds: readonly string[];
  readonly orderedSections: readonly string[];
  readonly aiSelectionOrder: readonly string[];
  readonly savedSessionProgramme: Record<string, string> | null;
  readonly aiResults: Record<string, SectionAiResult>;
  readonly aiSelectionsBySection: Record<string, SectionAiSelection>;
  readonly fileName: string;
  readonly templateId: string | null;
  readonly contentMode: string;
  readonly flowType: string;
  readonly formValues: {
    readonly sponsorName: string;
    readonly administratorName: string;
    readonly notes: string;
  };
};

const clampStep = (value: number): GenerateWizardStep => {
  if (value <= 1) return 1;
  if (value >= 5) return 5;
  return value as GenerateWizardStep;
};

export const hydrateGenerationSession = (
  session: KbGenerationSessionDetail,
): HydratedGenerationSessionState => {
  const programme = session.programme ?? null;
  const sectionIds =
    session.selectedSectionIds.length > 0 ? [...session.selectedSectionIds] : [];
  const ordered =
    session.sectionOrder.length > 0 ? [...session.sectionOrder] : sectionIds;
  const aiSelections = session.aiSelections ?? {};
  const aiSelectionOrder =
    session.currentStep >= 4
      ? deriveAiSelectionOrder(ordered, aiSelections, sectionIds)
      : [];

  return {
    sessionId: session.id,
    step: clampStep(session.currentStep),
    gameRuleTitle: session.intentText ?? "",
    gameRuleName: session.ruleName ?? "",
    selectedSectionIds: sectionIds,
    orderedSections: ordered,
    aiSelectionOrder,
    savedSessionProgramme: programme,
    aiResults: session.aiResults ?? {},
    aiSelectionsBySection: aiSelections,
    fileName: session.fileName ?? "",
    templateId: session.templateId,
    contentMode: session.contentMode,
    flowType: session.flowType,
    formValues: {
      sponsorName: programme?.sponsor ?? "",
      administratorName: programme?.admin ?? "",
      notes: session.reviewerNotes ?? "",
    },
  };
};
